/**
 * 편집 권한이 있는 사용자용 카드 액션.
 *
 *   공개  → 검토 대기 글을 공개한다. 검토 대기 카드에만 붙는다.
 *   검토  → 공개를 되돌려 검토 대기로 내린다. 공개된 카드에만 붙는다
 *           (임시글과 달리 검토자 목록에는 남는다).
 *   임시  → 글을 draft 로 내린다.
 *   상세  → 본문(인스타 캡션 원문)을 창으로 띄운다. 글을 바꾸지 않는 유일한 동작이다.
 *
 * 관리 줄 자체를 서버가 권한에 따라 그리므로, 여기서는 버튼이 있을 때만 동작한다.
 * REST 쿠키 인증이라 요청마다 X-WP-Nonce 를 보낸다.
 *
 * 필터가 바뀌면 그리드가 통째로 교체되고 카드는 홈 그리드·찜 목록 어디에나
 * 나타나므로, 특정 목록이 아니라 문서에 위임한다.
 */
const InitAcademyAdmin = () => {
  const config = window.qrshopAcademy;

  if (!config?.restUrl || !config?.nonce) {
    return;
  }

  const request = async (path, { method = 'POST', body } = {}) => {
    const init = { method, headers: { 'X-WP-Nonce': config.nonce } };

    // GET 은 본문을 싣지 않는다 — 빈 본문이라도 보내면 브라우저가 요청을 거부한다.
    if (method !== 'GET') {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body ?? {});
    }

    const response = await fetch(`${config.restUrl}${path}`, init);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || '요청에 실패했습니다.');
    }

    return data;
  };

  const setBusy = (panel, busy) => {
    panel.querySelectorAll('button, input').forEach((el) => {
      el.disabled = busy;
    });
  };

  const setStatus = (panel, message) => {
    const status = panel.querySelector('[data-academy-admin-status]');
    status.textContent = message;
    status.hidden = message === '';
  };

  const removeCard = (panel) => {
    panel.closest('.academy-card')?.remove();
  };

  /**
   * 서버가 다시 그려 준 카드로 이 카드 한 장만 바꾼다.
   *
   * 상태가 바뀌면 알약·버튼 문구가 한꺼번에 바뀐다. 그걸 여기서 조각조각 맞춰 고치면
   * 카드 렌더 규칙이 두 벌이 되므로, 서버가 만든 마크업을 그대로 받아 끼운다
   * (목록 REST 와 같은 방식이다).
   *
   * 목록 전체를 다시 불러오지 않는 이유는 검토 흐름 때문이다 — 한 장 처리할 때마다
   * 그리드가 새로 그려지면 보던 자리를 잃는다.
   */
  const replaceCard = (panel, html) => {
    const card = panel.closest('.academy-card');

    if (!card || !html) {
      return null;
    }

    card.insertAdjacentHTML('afterend', html);
    const next = card.nextElementSibling;
    card.remove();

    return next;
  };

  /**
   * 진행 상태를 '검토 대기' 알약 자리에서 보여준다.
   *
   * 공개는 그 알약이 사라지는 동작이라, 관리 줄 아래에 글자가 한 줄 떴다 사라지는
   * 것보다 알약이 그대로 '공개하는 중…' → '공개 완료'로 바뀌는 편이 눈이 덜 튄다.
   * 카드가 서버 마크업으로 교체되어도 같은 자리에 이어 붙일 수 있게, 카드든 관리
   * 줄이든 받아 준다.
   *
   * 공개된 카드에는 알약 자체가 없으므로 그때는 잠깐 만들어 쓰고 지운다 —
   * 서버가 그리지 않는 표시라 남겨 두면 목록이 다시 그려질 때 사라져 버린다.
   */
  const setFlag = (node, message, modifier) => {
    const row = node?.matches('[data-academy-admin]')
      ? node
      : (node?.querySelector('[data-academy-admin]') ?? null);

    if (!row) {
      return null;
    }

    let flag = row.querySelector('[data-academy-admin-flag]');

    if (!flag) {
      flag = document.createElement('span');
      flag.dataset.academyAdminFlag = '';
      flag.setAttribute('role', 'status');
      row.prepend(flag);
    }

    flag.className = `academy-card__admin-flag academy-card__admin-flag--${modifier}`;
    flag.textContent = message;

    return flag;
  };

  /**
   * 원문 보기 창 — 서버가 푸터에 하나만 그려 둔다(inc/academy.php).
   *
   * 창이 없으면(권한이 없거나 옛 마크업) '상세'는 아무 일도 하지 않는다.
   */
  const detail = document.querySelector('[data-academy-detail-dialog]');
  const detailParts = detail && {
    title: detail.querySelector('[data-academy-detail-title]'),
    body: detail.querySelector('[data-academy-detail-body]'),
    error: detail.querySelector('[data-academy-detail-error]'),
    edit: detail.querySelector('[data-academy-detail-edit]'),
    source: detail.querySelector('[data-academy-detail-source]'),
  };

  /** 주소가 있을 때만 보이는 링크(편집 화면·원본 보기). */
  const setDetailLink = (node, url) => {
    if (!node) {
      return;
    }

    node.hidden = !url;

    if (url) {
      node.href = url;
    }
  };

  const setDetailError = (message) => {
    if (!detailParts?.error) {
      return;
    }

    detailParts.error.textContent = message || '';
    detailParts.error.hidden = !message;
  };

  /**
   * 본문을 넣는다 — 줄바꿈은 <br> 로 직접 만든다.
   *
   * CSS(white-space: pre-wrap) 하나에 기대면 스타일이 한 판만 어긋나도 원문이 통째로
   * 한 줄이 되어 검토가 불가능해진다. 이 창에서 줄바꿈은 장식이 아니라 내용이라
   * 마크업으로 못박는다(CSS 는 연속 공백을 살리는 몫만 남는다).
   *
   * 글자는 텍스트 노드로만 넣는다 — 원문에 태그가 섞여 있어도 글자로 보여야 하고,
   * 검토자는 실제로 무엇이 저장돼 있는지를 봐야 한다.
   */
  const fillBody = (node, text) => {
    node.textContent = '';

    String(text)
      .split(/\r\n|\r|\n/)
      .forEach((line, index) => {
        if (index > 0) {
          node.appendChild(document.createElement('br'));
        }

        node.appendChild(document.createTextNode(line));
      });
  };

  /**
   * 창을 먼저 열고 내용을 채운다.
   *
   * 캡션을 받아온 뒤에 열면 누른 뒤 잠깐 아무 반응이 없어, 눌리지 않은 것처럼 보인다.
   */
  const openDetail = async (postId) => {
    if (!detail || typeof detail.showModal !== 'function') {
      return;
    }

    detailParts.title.textContent = '';
    detailParts.body.textContent = '불러오는 중…';
    setDetailError('');
    setDetailLink(detailParts.edit, '');
    setDetailLink(detailParts.source, '');

    if (!detail.open) {
      detail.showModal();
    }

    try {
      const data = await request(`/manage/${postId}/detail`, { method: 'GET' });

      detailParts.title.textContent = data?.title || `#${postId}`;
      fillBody(detailParts.body, data?.content?.trim() ? data.content : '본문이 비어 있습니다.');

      setDetailLink(detailParts.edit, data?.editUrl);
      setDetailLink(detailParts.source, data?.sourceUrl);

      detailParts.body.scrollTop = 0;
    } catch (error) {
      detailParts.body.textContent = '';
      setDetailError(error.message);
    }
  };

  detail?.querySelector('[data-academy-detail-close]')?.addEventListener('click', () => {
    detail.close();
  });

  // 바깥(백드롭)을 눌러도 닫는다 — 안쪽 상자를 누른 경우는 제외한다.
  detail?.addEventListener('click', (event) => {
    if (event.target === detail) {
      detail.close();
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest(
      '[data-academy-admin-draft], [data-academy-admin-publish], [data-academy-admin-pending], [data-academy-admin-detail]'
    );

    if (!button) {
      return;
    }

    const panel = button.closest('[data-academy-admin]');

    // 원문 보기 — 글을 바꾸지 않으므로 카드도 건드리지 않는다.
    if (button.matches('[data-academy-admin-detail]')) {
      await openDetail(panel.dataset.academyAdmin);
      return;
    }

    // 공개 — 되돌리기 쉬운 동작이라 확인 없이 바로 처리한다.
    if (button.matches('[data-academy-admin-publish]')) {
      // 알약 색은 검토 순서를 고르는 표시다(빨강/파랑). 실패해서 되돌릴 때 이 구분이
      // 사라지면 그 카드만 다른 뜻으로 읽히므로, 어느 쪽이었는지 먼저 기억해 둔다.
      const flagNode = panel.querySelector('[data-academy-admin-flag]');
      const flagBefore = flagNode?.classList.contains('academy-card__admin-flag--rich')
        ? 'rich'
        : 'pending';
      // 문구에는 신뢰도가 붙어 있을 수 있다('검토 : 0.85') — 되돌릴 때 그대로 돌려놓는다.
      const flagTextBefore = flagNode?.textContent.trim() || '검토';

      setBusy(panel, true);
      setFlag(panel, '공개하는 중…', 'busy');

      try {
        const result = await request(`/manage/${panel.dataset.academyAdmin}/publish`);

        // 카드가 교체되면 panel 은 문서에서 떨어져 나간다 — 이후 조작은 새 카드에 한다.
        const card = replaceCard(panel, result?.html);
        const done = setFlag(card ?? panel, '공개 완료', 'done');

        // 결과 표시는 잠깐이면 된다. 공개된 카드가 계속 무언가를 알리고 있으면
        // 다음에 검토할 카드를 고르는 데 방해가 된다.
        window.setTimeout(() => done?.remove(), 1600);

        if (card) {
          return;
        }

        // 마크업을 못 받았을 때(구버전 서버 등)도 멈추지는 않게 한다.
        setStatus(panel, '목록을 새로고침하면 반영됩니다.');
      } catch (error) {
        setFlag(panel, flagTextBefore, flagBefore);
        setStatus(panel, error.message);
      }

      setBusy(panel, false);
      return;
    }

    // 검토 대기로 되돌리기 — 공개의 반대. 마찬가지로 되돌리기 쉬워 확인 없이 처리한다.
    if (button.matches('[data-academy-admin-pending]')) {
      setBusy(panel, true);
      // 공개된 카드에는 알약이 없다 — 이 알약은 여기서 만든 것이라 실패하면 도로 지운다.
      const flag = setFlag(panel, '되돌리는 중…', 'busy');

      try {
        const result = await request(`/manage/${panel.dataset.academyAdmin}/pending`);
        const card = replaceCard(panel, result?.html);

        // 되돌아온 카드에는 서버가 '검토 대기' 알약을 다시 그려 준다 — 따로 표시하지 않는다.
        if (card) {
          return;
        }

        setStatus(panel, '목록을 새로고침하면 반영됩니다.');
      } catch (error) {
        flag?.remove();
        setStatus(panel, error.message);
      }

      setBusy(panel, false);
      return;
    }

    // 임시글로 내리기 — 목록에서 사라지는 동작이라 한 번 확인한다.
    if (!window.confirm('이 클래스를 임시글로 내릴까요? 목록에서 사라집니다.')) {
      return;
    }

    setBusy(panel, true);
    setStatus(panel, '처리 중…');

    try {
      await request(`/manage/${panel.dataset.academyAdmin}/draft`);
      removeCard(panel);
    } catch (error) {
      setStatus(panel, error.message);
      setBusy(panel, false);
    }
  });
};

export default InitAcademyAdmin;
